import { z } from 'zod'
import { isDefined } from '@omnea-app/packages/universal/type-guards/isDefined'

export const httpHeaderSchema = z.object({
  header: z.string(),
  value: z.string(),
})
export type HttpHeader = z.infer<typeof httpHeaderSchema>

const methods = z.enum(['POST', 'PUT', 'PATCH', 'GET', 'DELETE'])
export const httpMethods = methods.enum
export type HttpMethod = z.infer<typeof methods>

export const contentTypeSchema = z.enum(['form-data', 'json', 'form-urlencoded'])
export type ContentType = z.infer<typeof contentTypeSchema>

// Base schemas for form data
export const formDataEntryTypeSchema = z.enum(['text', 'file'])
// Schema for UI editing form data entries (used in form builders)
export const formDataEntrySchema = z.object({
  key: z.string(),
  type: formDataEntryTypeSchema,
  value: z.string(), // For text: actual value, for file: question ID
})
export type FormDataEntry = z.infer<typeof formDataEntrySchema>

export const fileObjectSchema = z.object({
  mimeType: z.string(),
  filename: z.string(),
  fileStorageId: z.string(),
})

export type FileObject = z.infer<typeof fileObjectSchema>

// Runtime form data value schema - used during execution with resolved file objects
export const formDataRuntimeValueSchema = z.discriminatedUnion('type', [
  z.object({
    key: z.string(),
    type: z.literal('text'),
    value: z.string(),
  }),
  z.object({
    key: z.string(),
    type: z.literal('file'),
    value: z.union([fileObjectSchema, z.array(fileObjectSchema)]),
  }),
])

export type FormDataRuntimeValue = z.infer<typeof formDataRuntimeValueSchema>

export const urlEncodedEntrySchema = z.object({
  id: z.string(),
  key: z.string(),
  value: z.string(),
})
// Mappings saved before these keys existed don't store them: the content type then reads as json.
export const externalOperationHttpMappingSchema = z.object({
  customIntegrationId: z.string().nullish(),
  method: methods,
  headers: z.array(httpHeaderSchema),
  contentType: contentTypeSchema.optional(),
  formData: z.array(formDataEntrySchema).optional(),
  urlEncodedFormData: z.array(urlEncodedEntrySchema).optional(),
})
export type ExternalOperationHttpMapping = z.infer<typeof externalOperationHttpMappingSchema>

export const isExternalOperationHttpMapping = (
  mapping: ExternalOperationHttpMapping | ExternalOperationSftpMapping
): mapping is ExternalOperationHttpMapping => {
  return 'method' in mapping
}

export const isCustomIntegrationMapping = (
  mapping: ExternalOperationHttpMapping | ExternalOperationSftpMapping | undefined
): mapping is ExternalOperationHttpMapping | ExternalOperationSftpMapping => {
  return isDefined(mapping) && Boolean(mapping.customIntegrationId)
}

export const externalOperationSftpMappingSchema = z.object({
  customIntegrationId: z.string().nullable(),
})
export type ExternalOperationSftpMapping = z.infer<typeof externalOperationSftpMappingSchema>
